import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from './client';

/** Client for /api/dmr — Digital Marketing Review call scheduling (#216). */

export type DmrAudience = 'admin' | 'fbc' | 'all';

export type DmrSeason = {
  id: number;
  name: string;
  registrationOpensOn: string;
  callsStartOn: string;
  callsEndOn: string;
  goalPct: number;
  weeklyTarget: number;
  runwayGoalWeeks: number;
  runwayAlertWeeks: number;
  audience: DmrAudience;
};

export type DmrWeek = { week: string; booked: number; open: number };

export type DmrSeasonResponse = {
  season: DmrSeason;
  viewer: { role: 'admin' | 'fbc' | 'franchisee'; name: string | null };
  invites: { live: boolean; reason: string | null; testMode: boolean };
  partnerSource?: 'zoho' | 'pulse';
  progress?: { partners: number; goal: number; scheduled: number; completed: number; noShow: number };
  runway?: { fromWeek: string; runwayWeeks: number; weeksRemaining: number };
  weeks?: DmrWeek[];
};

export type DmrAttendee = { name: string; email: string };

export type DmrBooking = {
  id: number;
  slotId: number | null;
  partnerKey: string;
  partnerName: string;
  afs: string[];
  primaryEmail: string;
  extraAttendees: DmrAttendee[];
  fbcName: string | null;
  inviteFbc: boolean;
  bookedBy: { role: string; name: string | null };
  status: 'scheduled' | 'completed' | 'no_show' | 'cancelled';
  rescheduleCount: number;
  teamsJoinUrl: string | null;
  inviteStatus: 'pending' | 'sent' | 'failed';
  inviteError: string | null;
  startsAt: string | null;
  durationMin: number | null;
  teamMember: { id: number; name: string } | null;
  createdAt: string;
  followupSentAt: string | null;
};

export type DmrSlot = {
  id: number;
  startsAt: string;
  durationMin: number;
  status: 'open' | 'booked' | 'blocked' | 'taken';
  blockedReason: string | null;
  teamMember: { id: number; name: string };
  booking: {
    id: number;
    partnerKey: string;
    partnerName: string;
    afs: string[];
    fbcName: string | null;
    status: string;
    inviteStatus: string;
  } | null;
};

/** Website chat rollout for one AF (#246). */
export type DmrChatRolloutStatus = 'not_started' | 'ready' | 'pilot' | 'live' | 'paused';
export type DmrChatStatus = {
  af: string;
  rolloutStatus: DmrChatRolloutStatus;
  enabled: boolean;
  seenOnSiteAt: string | null;
  backstopEmail: boolean;
  caregiverAppLink: boolean;
  agents: number;
  agentsVerified: number;
  sales: number;
  recruiting: number;
};

export type DmrPartner = {
  key: string;
  name: string;
  afs: string[];
  marketingNames: string[];
  email: string | null;
  coOwnerEmails: string[];
  fbc: { name: string; email: string } | null;
  mine: boolean;
  booking: DmrBooking | null;
  /** Staff only: least-advanced chat rollout status across the partner's AFs. */
  chatStatus?: DmrChatRolloutStatus;
};

export type DmrTemplateItem = {
  id: string;
  label: string;
  type: 'check' | 'choice' | 'number' | 'text';
  options?: string[];
};
export type DmrTemplate = {
  id: number;
  name: string;
  version: number;
  sections: Array<{ id: string; title: string; prompt?: string; items: DmrTemplateItem[] }>;
};
export type DmrResponse = { status?: 'yes' | 'needs_action' | 'na'; value?: string | number | null; note?: string };
export type DmrRun = {
  templateId: number;
  responses: Record<string, DmrResponse>;
  notes: string | null;
  summary: string | null;
  updatedBy: string | null;
  updatedAt: string;
};
export type DmrActionItem = {
  id: number;
  bookingId: number;
  partnerKey: string;
  partnerName: string | null;
  afs: string[] | null;
  templateItemId: string | null;
  title: string;
  detail: string | null;
  ownerSide: 'amada' | 'franchisee';
  ownerName: string | null;
  dueOn: string | null;
  status: 'open' | 'in_progress' | 'done' | 'wont_do';
  visibleToFranchisee: boolean;
  createdBy: string | null;
  completedAt: string | null;
};
export type DmrCallDetail = {
  booking: DmrBooking;
  partner: Omit<DmrPartner, 'mine' | 'booking'> | null;
  template: DmrTemplate | null;
  run: DmrRun | null;
  actionItems: DmrActionItem[];
  canEdit: boolean;
  chat: DmrChatStatus[];
  /** The AI recap from the Teams transcript is switched on (#246). */
  recapEnabled: boolean;
};

/** A draft only: nothing is stored until the team member approves it. */
export type DmrRecapDraft = {
  summary: string;
  actionItems: Array<{ title: string; detail: string; ownerSide: 'amada' | 'franchisee'; visibleToFranchisee: boolean }>;
};
export type DmrItemInput = Partial<
  Pick<DmrActionItem, 'title' | 'detail' | 'ownerSide' | 'ownerName' | 'dueOn' | 'status' | 'visibleToFranchisee' | 'templateItemId'>
>;

/** One call in an AF's yearly archive (#254). `year` is the year it took place. */
export type DmrPastCall = {
  id: number;
  year: number | null;
  seasonName: string;
  current: boolean;
  status: DmrBooking['status'];
  startsAt: string | null;
  teamMemberName: string | null;
  template: DmrTemplate | null;
  responses: Record<string, DmrResponse>;
  summary: string | null;
};

export type DmrTeamMember = { id: number; name: string; email: string | null; isActive: boolean; slots: number; weekly: DmrWeeklyTime[] };
/** One weekly start a team member takes: ISO weekday (2 = Tue) + Pacific 'HH:MM'. */
export type DmrWeeklyTime = { day: number; time: string };

export type DmrInviteResult = { status: 'sent' | 'pending' | 'failed'; error?: string } | null;

export type DmrReminderPlan = {
  send: boolean;
  reason: string;
  runwayWeeks: number;
  threshold: number;
  perFbc: Array<{ name: string; email: string; unbooked: number }>;
};

export const dmrApi = {
  season: () => apiGet<DmrSeasonResponse>('/api/dmr/season'),
  slots: () => apiGet<{ slots: DmrSlot[] }>('/api/dmr/slots'),
  partners: () => apiGet<{ partners: DmrPartner[] }>('/api/dmr/partners'),
  book: (body: {
    slotId: number;
    partnerKey: string;
    extraAttendees: DmrAttendee[];
    inviteFbc: boolean;
    primaryEmail?: string;
  }) => apiPost<{ booking: DmrBooking; invite: DmrInviteResult }>('/api/dmr/bookings', body),
  update: (id: number, body: { slotId?: number; extraAttendees?: DmrAttendee[]; inviteFbc?: boolean; status?: string }) =>
    apiPatch<{ booking: DmrBooking; invite: DmrInviteResult }>(`/api/dmr/bookings/${id}`, body),
  cancel: (id: number) => apiDelete<{ ok: true }>(`/api/dmr/bookings/${id}`),
  resendInvite: (id: number) => apiPost<{ invite: DmrInviteResult }>(`/api/dmr/bookings/${id}/resend-invite`),
  call: (id: number) => apiGet<DmrCallDetail>(`/api/dmr/calls/${id}`),
  history: (af: string) => apiGet<{ calls: DmrPastCall[] }>(`/api/dmr/history?af=${encodeURIComponent(af)}`),
  /** Only the fields sent are saved. */
  saveRun: (id: number, body: { responses?: Record<string, DmrResponse>; notes?: string; summary?: string }) =>
    apiPut<{ run: DmrRun }>(`/api/dmr/calls/${id}/run`, body),
  draftRecap: (id: number) => apiPost<{ draft: DmrRecapDraft }>(`/api/dmr/calls/${id}/recap`),
  setChatStatus: (id: number, af: string, status: DmrChatRolloutStatus) =>
    apiPut<{ chat: DmrChatStatus[] }>(`/api/dmr/calls/${id}/chat-status`, { af, status }),
  completeCall: (id: number) => apiPost<{ booking: DmrBooking }>(`/api/dmr/calls/${id}/complete`),
  sendFollowup: (id: number) => apiPost<{ sent: string[]; testMode: boolean }>(`/api/dmr/calls/${id}/followup`),
  actionItems: (openOnly = false) => apiGet<{ items: DmrActionItem[] }>(`/api/dmr/action-items${openOnly ? '?open=1' : ''}`),
  createItem: (bookingId: number, body: DmrItemInput) =>
    apiPost<{ item: DmrActionItem }>(`/api/dmr/calls/${bookingId}/action-items`, body),
  updateItem: (id: number, body: DmrItemInput) => apiPatch<{ item: DmrActionItem }>(`/api/dmr/action-items/${id}`, body),
  deleteItem: (id: number) => apiDelete<{ ok: true }>(`/api/dmr/action-items/${id}`),
  team: () => apiGet<{ team: DmrTeamMember[]; slotTimes: string[]; slotDays: number[] }>('/api/dmr/admin/team'),
  /** Exactly which day + time pairs this person takes. */
  setTimes: (id: number, weekly: DmrWeeklyTime[]) =>
    apiPut<{ removed: number; added: number }>(`/api/dmr/admin/team/${id}/times`, { weekly }),
  updateTeam: (id: number, body: { email?: string; isActive?: boolean }) =>
    apiPut<{ ok: true }>(`/api/dmr/admin/team/${id}`, body),
  generateSlots: () => apiPost<{ created: number }>('/api/dmr/admin/generate-slots'),
  setSlot: (id: number, body: { status: 'open' | 'blocked'; reason?: string }) =>
    apiPut<{ ok: true }>(`/api/dmr/admin/slots/${id}`, body),
  setAudience: (audience: DmrAudience) => apiPut<{ ok: true }>('/api/dmr/admin/season', { audience }),
  reminderPlan: () => apiGet<DmrReminderPlan>('/api/dmr/admin/reminder'),
  sendPendingInvites: () =>
    apiPost<{ sent: number; pending: number; failed: number }>('/api/dmr/admin/send-pending-invites'),
};
